import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { SummaryCard } from "../components/home/SummaryCard";
import { UpcomingBills } from "../components/home/UpcomingBills";
import { IncomeVsExpensesChart } from "../components/home/IncomeVsExpensesChart";
import { PendingPayments } from "../components/home/PendingPayments";
import { MonthlyHistoryTable } from "../components/home/MonthlyHistoryTable";
import { MonthlyHistoryCards } from "../components/home/MonthlyHistoryCards";
import { CurrencyTotals } from "../components/home/CurrencyTotals";
import { useControlCash } from "../store/controlCashStore";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { formatAmountByCurrency, getCurrencySymbol } from "../utils/currency";

const calcChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const {
    dispatch,
    state,
    getMonthTotals,
    getMonthTotalsByCurrency,
    getMonthTotalsEstimated,
    getMonthExpenses,
    getUpcomingBills,
    getMonthsSortedByDate,
    formatDisplayAmount,
    isDataLoading,
    dataError
  } = useControlCash();
  const { confirm } = useConfirm();

  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currencySymbol = state.currencySymbol ?? "S/";
  const formatAmount = formatDisplayAmount;

  const handleDeleteMonth = async (e, month) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Eliminar mes",
      message: `¿Estás seguro de eliminar "${month.name}"?\n\nSe eliminarán también los ingresos y gastos de este mes.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({ type: "DELETE_MONTH", payload: month.id });
    }
  };

  const sortedMonths = getMonthsSortedByDate();

  // Determinar cuál es el mes actual según la fecha del sistema
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthNumber = today.getMonth() + 1; // getMonth() devuelve 0-11

  // Encontrar el índice del mes actual en la lista ordenada
  const findCurrentMonthIndex = () => {
    const index = sortedMonths.findIndex(m => m.year === currentYear && m.monthNumber === currentMonthNumber);
    return index >= 0 ? index : 0; // Si no existe, usar el primero
  };

  const months = sortedMonths.map((month) => {
    const totalsByCurrency = getMonthTotalsByCurrency(month.id);
    const totalsEstimated = getMonthTotalsEstimated(month.id, state.currency || "PEN");
    const pendingExpenses = getMonthExpenses(month.id).filter((e) => e.status === "PENDING");
    const isCurrentMonth = month.year === currentYear && month.monthNumber === currentMonthNumber;
    return {
      id: month.id,
      name: month.name,
      year: month.year,
      monthNumber: month.monthNumber,
      totalsByCurrency,
      totalsEstimated,
      pendientes: pendingExpenses.length,
      pendingExpenses,
      totalPending: totalsEstimated.totalPending ?? 0,
      isCurrentMonth
    };
  });

  const initialMonthIndex = findCurrentMonthIndex();
  const [selectedMonthIndex, setSelectedMonthIndex] = React.useState(initialMonthIndex);
  
  // Actualizar el índice si cambia el mes actual
  React.useEffect(() => {
    const newIndex = findCurrentMonthIndex();
    if (newIndex !== selectedMonthIndex && months[newIndex]?.isCurrentMonth) {
      setSelectedMonthIndex(newIndex);
    }
  }, [sortedMonths.length, currentYear, currentMonthNumber]);
  
  const currentMonth = months[selectedMonthIndex] ?? null;
  const previousMonth = months[selectedMonthIndex + 1] ?? null;
  const hasPreviousMonths = selectedMonthIndex < months.length - 1;
  const hasNextMonths = selectedMonthIndex > 0;

  // Obtener totales por moneda del mes seleccionado
  const currentTotalsByCurrency = currentMonth?.totalsByCurrency || {};

  const upcomingBills = getUpcomingBills();
  const shortMonthName = (name) => {
    if (!name) return "";
    const firstWord = name.split(" ")[0] || "";
    return firstWord.length >= 3 ? firstWord.slice(0, 3) : firstWord;
  };

  const chartDataFormatted = months
    .slice(0, 6)
    .reverse()
    .map((m) => ({
      month: shortMonthName(m.name),
      income: m.totalsEstimated?.income || 0,
      expenses: m.totalsEstimated?.totalCommitted || 0
    }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Welcome */}
        <div className="mb-10 md:mb-12">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div className="flex-1">
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
                Bienvenido, {user?.name?.split(" ")[0] || "Usuario"}
              </h1>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                Resumen financiero
              </p>
            </div>
            {currentMonth && (
              <button
                onClick={() => navigate(`/month/${currentMonth.id}`)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2 whitespace-nowrap"
                aria-label="Ver detalle del mes"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span className="hidden sm:inline">Ver detalle</span>
                <span className="sm:hidden">Ver</span>
              </button>
            )}
          </div>

          {/* Selector de mes */}
          {months.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between gap-4">
                {/* Flecha izquierda: Meses más antiguos */}
                <button
                  onClick={() => setSelectedMonthIndex(Math.min(selectedMonthIndex + 1, months.length - 1))}
                  disabled={!hasPreviousMonths}
                  className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                    hasPreviousMonths
                      ? "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-pointer"
                      : "bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50"
                  }`}
                  aria-label="Mes anterior (más antiguo)"
                  title="Mes anterior"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="flex-1 text-center">
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {currentMonth?.isCurrentMonth && (
                      <span className="px-2 py-1 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                        Actual
                      </span>
                    )}
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                      {currentMonth?.name || "Sin mes"}
                    </h2>
                  </div>
                  {months.length > 1 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {selectedMonthIndex + 1} de {months.length} {months.length === 1 ? "mes" : "meses"}
                    </p>
                  )}
                </div>

                {/* Flecha derecha: Meses más nuevos */}
                <button
                  onClick={() => setSelectedMonthIndex(Math.max(selectedMonthIndex - 1, 0))}
                  disabled={!hasNextMonths}
                  className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                    hasNextMonths
                      ? "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-pointer"
                      : "bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50"
                  }`}
                  aria-label="Mes siguiente (más nuevo)"
                  title="Mes siguiente"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {dataError && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
            Error al cargar datos: {dataError}
          </div>
        )}

        {isDataLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
          </div>
        ) : !currentMonth ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-12 text-center mb-8">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No hay meses registrados</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Crea tu primer mes para comenzar a controlar tus finanzas</p>
            <button
              onClick={() => navigate("/month/new")}
              className="inline-flex items-center gap-2 px-10 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuevo Mes
            </button>
          </div>
        ) : (
          <>
            {isMobile ? (
              /* Mobile layout */
              <>
                {/* Totales por moneda */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Totales por Moneda
                  </h2>
                  <CurrencyTotals
                    totalsByCurrency={currentTotalsByCurrency}
                    exchangeRates={state.exchangeRates || {}}
                    baseCurrency={state.currency || "PEN"}
                  />
                </section>

                <section className="mb-8">
                  <UpcomingBills
                    bills={upcomingBills}
                    formatAmount={formatAmount}
                    onBillClick={(b) => b.monthId && navigate(`/month/${b.monthId}`)}
                  />
                </section>

                <section className="mb-8">
                  <IncomeVsExpensesChart
                    data={chartDataFormatted}
                    formatAmount={formatAmount}
                    isDark={isDark}
                  />
                </section>

                <section className="mb-8">
                  <PendingPayments
                    expenses={currentMonth.pendingExpenses}
                    formatAmount={(amount, hideValue, currency) => formatDisplayAmount(amount, hideValue, currency)}
                    totalPending={currentMonth.totalPending}
                    monthId={currentMonth.id}
                    monthYear={currentMonth.year}
                    monthNumber={currentMonth.monthNumber}
                    customCategories={state.customCategories || []}
                    defaultExpanded={false}
                  />
                </section>

                <section className="mb-8">
                  <MonthlyHistoryCards
                    history={months}
                    formatAmount={formatAmount}
                    onDeleteMonth={handleDeleteMonth}
                  />
                </section>

                <section className="pb-6">
                  <button
                    onClick={() => navigate("/month/new")}
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 transition-all text-base"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Nuevo Mes
                  </button>
                </section>
              </>
            ) : (
              /* Desktop layout */
              <>
                {/* Totales por moneda */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Totales por Moneda
                  </h2>
                  <CurrencyTotals
                    totalsByCurrency={currentTotalsByCurrency}
                    exchangeRates={state.exchangeRates || {}}
                    baseCurrency={state.currency || "PEN"}
                  />
                </section>

                <section className="mb-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <UpcomingBills
                      bills={upcomingBills}
                      formatAmount={formatAmount}
                      onBillClick={(b) => b.monthId && navigate(`/month/${b.monthId}`)}
                    />
                    <IncomeVsExpensesChart
                      data={chartDataFormatted}
                      formatAmount={formatAmount}
                      isDark={isDark}
                    />
                  </div>
                </section>

                <section className="mb-12">
                  <PendingPayments
                    expenses={currentMonth.pendingExpenses}
                    formatAmount={(amount, hideValue, currency) => formatDisplayAmount(amount, hideValue, currency)}
                    totalPending={currentMonth.totalPending}
                    monthId={currentMonth.id}
                    monthYear={currentMonth.year}
                    monthNumber={currentMonth.monthNumber}
                    customCategories={state.customCategories || []}
                    defaultExpanded={false}
                  />
                </section>

                <section className="mb-12">
                  <MonthlyHistoryTable
                    history={months}
                    formatAmount={formatAmount}
                    onDeleteMonth={handleDeleteMonth}
                  />
                </section>

                <section className="flex justify-center pt-4 pb-8">
                  <button
                    onClick={() => navigate("/month/new")}
                    className="inline-flex items-center gap-2 px-10 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all text-base"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Nuevo Mes
                  </button>
                </section>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Home;
